/**
 * Per-class runway performance for the motion sim.
 *
 * Bigger/heavier aircraft need more runway: a light piston floats on in a few
 * hundred feet, an ultra-long-range jet eats most of the runway on takeoff. We
 * scale the landing rollout and takeoff roll by aircraft category (falling back
 * to a length-based estimate), then place the per-aircraft touchdown / rollout /
 * rotation points along the authored runway. A longer rollout also makes the
 * ground router (`routeGround`) snap the runway exit to a taxiway farther down
 * the field, so heavies vacate later than light traffic — the realistic look.
 */
import { headingToForward, type Pt } from './easing'
import type { FieldGraph } from '../data/fieldGraph'
import type { MotionConfig } from '../data/motionConfig'

export interface RunwayPerf {
  /** Ground distance from touchdown to a comfortable taxi speed, feet. */
  landingRollFt: number
  /** Ground distance from brake-release to rotation/liftoff, feet. */
  takeoffRollFt: number
}

/** Representative ground-roll distances by aircraft category (feet). */
const BY_CATEGORY: Record<string, RunwayPerf> = {
  single_engine_piston: { landingRollFt: 700, takeoffRollFt: 900 },
  multi_engine_piston: { landingRollFt: 1100, takeoffRollFt: 1400 },
  turboprop: { landingRollFt: 1600, takeoffRollFt: 2000 },
  very_light_jet: { landingRollFt: 2300, takeoffRollFt: 2700 },
  light_jet: { landingRollFt: 2800, takeoffRollFt: 3300 },
  midsize_jet: { landingRollFt: 3100, takeoffRollFt: 3900 },
  super_midsize_jet: { landingRollFt: 3400, takeoffRollFt: 4500 },
  large_jet: { landingRollFt: 3700, takeoffRollFt: 5200 },
  ultra_long_range: { landingRollFt: 4100, takeoffRollFt: 6200 },
  helicopter: { landingRollFt: 250, takeoffRollFt: 250 },
}

/**
 * Resolve roll distances for an aircraft. Prefers the catalogued `category`;
 * otherwise scales a mid-size baseline by the aircraft length (≈60 ft = 1×).
 */
export function runwayPerf(category: string | null | undefined, lengthFt?: number): RunwayPerf {
  if (category && BY_CATEGORY[category]) return BY_CATEGORY[category]
  const k = Math.max(0.5, Math.min(3, (lengthFt ?? 60) / 60))
  return { landingRollFt: 1500 * k, takeoffRollFt: 2000 * k }
}

const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])
const along = (p: Pt, d: Pt, m: number): Pt => [p[0] + d[0] * m, p[1] + d[1] * m]

/**
 * Place the per-aircraft touchdown / rollout-end / rotation points along the
 * authored runway, clamped so they never overrun the opposite threshold.
 * Returns the FieldGraph's own (synth) points unchanged when there is no real
 * runway length to work with.
 */
export function rollGeometryFor(
  graph: FieldGraph,
  perf: RunwayPerf,
  cfg: MotionConfig
): { touchdownPoint: Pt; rolloutEnd: Pt; rotationPoint: Pt } {
  const runwayLen = dist(graph.thresholdApproach, graph.thresholdDeparture)
  if (runwayLen < 1) {
    return {
      touchdownPoint: graph.touchdownPoint,
      rolloutEnd: graph.rolloutEnd,
      rotationPoint: graph.rotationPoint,
    }
  }
  const aFwd = headingToForward(graph.approachBearingDeg) // down-runway on landing
  const dFwd = headingToForward(graph.departureBearingDeg) // down-runway on takeoff

  const tdZone = Math.min(cfg.touchdownZoneFt, runwayLen * 0.2)
  const touchdownPoint = along(graph.thresholdApproach, aFwd, tdZone)
  const maxRollout = Math.max(50, runwayLen - tdZone - 150)
  const rolloutEnd = along(touchdownPoint, aFwd, Math.min(perf.landingRollFt, maxRollout))

  const maxTakeoff = Math.max(50, runwayLen - 150)
  const rotationPoint = along(graph.thresholdDeparture, dFwd, Math.min(perf.takeoffRollFt, maxTakeoff))

  return { touchdownPoint, rolloutEnd, rotationPoint }
}
